import React, { useCallback } from "react";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import Button from "@amzn/awsui-components-react/polaris/button";

import { setV2Enabled } from "./featureFlag";
import { trackButtonClick } from "../telemetry/telemetry";
import { resetOnboarding } from "../appShell/onboardingPreference";

/**
 * Rendered only in Legacy `<Content/>`. Tells users who opted back to
 * the old view that "Open filters" will take them to the new view and
 * show them how filtering works there, and that "Visit old view" (the
 * toolbar toggle) brings them back.
 *
 * Mirrors `V2Toggle`'s Legacy -> V2 path: flip the flag, flush
 * telemetry, full reload so `App` re-reads the flag synchronously.
 * Before flipping we clear the onboarding "seen" flag so the how-to
 * banner appears once more on the other side.
 */
export const LegacyViewNotice: React.FC = () => {
  const onOpenFilters = useCallback(async () => {
    resetOnboarding();
    setV2Enabled(true);
    try {
      await trackButtonClick("appShell", "legacyOpenFilters", {
        from: "legacy",
        to: "v2",
      });
    } catch {
      // Telemetry failures never block the user-visible flip.
    }
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, []);

  return (
    <Alert
      data-testid="legacy-view-notice"
      type="info"
      header="You're using the old view"
      action={
        <Button
          data-testid="legacy-view-open-filters"
          iconName="search"
          onClick={onOpenFilters}
        >
          Open filters
        </Button>
      }
    >
      Click <b>Open filters</b> to switch to the new view and see how to add
      filters there &mdash; just click the filter bar and start typing. You can
      come back any time with <b>Visit old view</b> in the top-right toolbar.
    </Alert>
  );
};

export default LegacyViewNotice;
